import { fetchAddonServicesThunk } from 'actions/addonServices/AddonServicesActions';
import { fetchMarketplaceBySlugThunk } from 'actions/marketplace/MarketplaceActions';
import { fetchMasterDataThunk } from 'actions/masterData/MasterDataActions';
import { resetPaymentMethods } from 'actions/paymentMethods/PaymentMethodsActions';
import { resetOnlineBookingPromocode } from 'actions/promocodes/OnlineBookingPromocodeActions';
import {
    fetchBusinessAreasWithServicesThunk,
    fetchServicesThunk
} from 'actions/services/ServicesActions';
import { fetchStaffsThunk } from 'actions/staffs/StaffsActions';
import { getOnlineBookingDepositRequired } from 'reducers/onlineBooking/OnlineBookingSelectors';
import { ThunkAction } from 'redux-thunk';
import { RootState } from 'store';
import { RootAction } from 'types/Actions';
import { AddonService } from 'types/AddonService';
import { PendingAgreementSignature } from 'types/Agreement';
import { Customer } from 'types/Customer';
import { Marketplace, MarketplaceServiceCategory } from 'types/Marketplace';
import { Service } from 'types/Service';
import { Staff } from 'types/Staff';
import {
    ONLINE_BOOKING_ADD_AGREEMENT_SIGNATURE,
    ONLINE_BOOKING_AGREEMENT_SIGNATURE_PERSISTED,
    ONLINE_BOOKING_AGREEMENT_SIGNATURE_UPDATED,
    ONLINE_BOOKING_ERROR,
    ONLINE_BOOKING_INIT,
    ONLINE_BOOKING_INIT_SUCCESS,
    ONLINE_BOOKING_NAVIGATE_TO_STEP,
    ONLINE_BOOKING_REMOVE_BOOKING_ITEMS,
    ONLINE_BOOKING_SET_BOOKING_ITEMS,
    ONLINE_BOOKING_SET_CHECKOUT_VIEW,
    ONLINE_BOOKING_SET_CUSTOMER,
    ONLINE_BOOKING_SET_EVALUATION_SERVICE_CATEGORY,
    ONLINE_BOOKING_SET_NOTES,
    ONLINE_BOOKING_SET_SELECTED_CATEGORY,
    ONLINE_BOOKING_SET_SELECTED_STAFF,
    ONLINE_BOOKING_UPDATE_BOOKING_ITEM,
    ONLINE_BOOKING_UPDATE_BOOKING_ITEMS,
    OnlineBookingAction
} from 'actions/onlineBooking/OnlineBookingActionsTypes';
import { CheckoutView, OnlineBookingItem, OnlineBookingStep } from 'types/OnlineBooking';

export const initOnlineBookingThunk = (
    marketplaceSlug: string
): ThunkAction<
    Promise<{
        marketplace: Marketplace;
        staffs: Array<Staff>;
        services: Array<Service>;
        addonServices: Array<AddonService>;
    }>,
    RootState,
    null,
    RootAction
> => {
    return async dispatch => {
        try {
            dispatch({ type: ONLINE_BOOKING_INIT });
            dispatch(resetPaymentMethods());
            dispatch(resetOnlineBookingPromocode());

            const marketplace = await dispatch(fetchMarketplaceBySlugThunk(marketplaceSlug));

            await dispatch(fetchMasterDataThunk(marketplace.id));

            const [services, addonServices, staffs] = await Promise.all([
                dispatch(fetchServicesThunk(marketplace.id)),
                dispatch(fetchAddonServicesThunk(marketplace.id)),
                dispatch(fetchStaffsThunk(marketplace.id)),
                dispatch(fetchBusinessAreasWithServicesThunk(marketplace.id))
            ]);

            dispatch({ type: ONLINE_BOOKING_INIT_SUCCESS });

            return {
                marketplace,
                staffs,
                services,
                addonServices
            };
        } catch (error) {
            dispatch({ type: ONLINE_BOOKING_ERROR });

            throw error;
        }
    };
};

export const navigateToStep = (
    step: OnlineBookingStep
): ThunkAction<void, RootState, null, RootAction> => {
    return async dispatch => {
        dispatch({ type: ONLINE_BOOKING_NAVIGATE_TO_STEP, payload: { step } });
    };
};

export const navigateAfterOnlineBookingCreatedThunk = (): ThunkAction<
    Promise<void>,
    RootState,
    null,
    RootAction
> => {
    return async (dispatch, getState) => {
        const state = getState();
        const marketplace = state.marketplace.data;
        const { customer } = state.onlineBooking;

        if (getOnlineBookingDepositRequired(state)) {
            dispatch(navigateToStep(OnlineBookingStep.Checkout));
            return;
        }

        // Always open Checkout when CoF is on. Deciding Success here races with
        // in-flight payment-method fetches and flashes the Success step first.
        if (marketplace.settings.cardOnFileEnabled && customer.id) {
            dispatch(resetPaymentMethods());
            dispatch(navigateToStep(OnlineBookingStep.Checkout));
            return;
        }

        dispatch(navigateToStep(OnlineBookingStep.Success));
    };
};

export const setOnlineBookingSelectedCategory = (
    category: MarketplaceServiceCategory
): OnlineBookingAction => ({
    type: ONLINE_BOOKING_SET_SELECTED_CATEGORY,
    payload: { category }
});

export const setOnlineBookingEvaluationServiceCategory = (
    category?: MarketplaceServiceCategory
): OnlineBookingAction => ({
    type: ONLINE_BOOKING_SET_EVALUATION_SERVICE_CATEGORY,
    payload: { category }
});

export const setOnlineBookingItems = (items: Array<OnlineBookingItem>): OnlineBookingAction => ({
    type: ONLINE_BOOKING_SET_BOOKING_ITEMS,
    payload: { items }
});

export const setOnlineBookingCustomer = (customer: Customer): OnlineBookingAction => ({
    type: ONLINE_BOOKING_SET_CUSTOMER,
    payload: { customer }
});

export const updateOnlineBookingBookingItem = (
    uuid: string,
    options: Partial<OnlineBookingItem>
): OnlineBookingAction => ({
    type: ONLINE_BOOKING_UPDATE_BOOKING_ITEM,
    payload: { uuid, options }
});

export const updateOnlineBookingBookingItems = (
    items: Array<OnlineBookingItem>
): OnlineBookingAction => ({
    type: ONLINE_BOOKING_UPDATE_BOOKING_ITEMS,
    payload: { items }
});

export const removeOnlineBookingBookingItems = (uuids: Array<string>): OnlineBookingAction => ({
    type: ONLINE_BOOKING_REMOVE_BOOKING_ITEMS,
    payload: { uuids }
});

export const setOnlineBookingSelectedStaff = (staff?: Staff): OnlineBookingAction => ({
    type: ONLINE_BOOKING_SET_SELECTED_STAFF,
    payload: { staff }
});

export const addOnlineBookingAgreementSignature = (
    signature: PendingAgreementSignature
): OnlineBookingAction => {
    return {
        type: ONLINE_BOOKING_ADD_AGREEMENT_SIGNATURE,
        payload: { signature }
    };
};

export const markOnlineBookingAgreementSignaturePersisted = (
    agreementId: number
): OnlineBookingAction => {
    return {
        type: ONLINE_BOOKING_AGREEMENT_SIGNATURE_PERSISTED,
        payload: { agreementId }
    };
};

export const updateOnlineBookingAgreementSignature = (
    agreementId: number,
    changes: Pick<PendingAgreementSignature, 'signature' | 'saveFailed'>
): OnlineBookingAction => {
    return {
        type: ONLINE_BOOKING_AGREEMENT_SIGNATURE_UPDATED,
        payload: { agreementId, changes }
    };
};

export const setOnlineBookingNotes = (notes?: string): OnlineBookingAction => ({
    type: ONLINE_BOOKING_SET_NOTES,
    payload: { notes }
});

export const setOnlineBookingCheckoutView = (view: CheckoutView): OnlineBookingAction => ({
    type: ONLINE_BOOKING_SET_CHECKOUT_VIEW,
    payload: { view }
});
